/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {EntityTreeModel} from '../../models/entity-tree.model';
import {EntityTreeUtilService} from '../entity-tree-util.service';
import {EntityTreeBranchModel} from '../../models/entity-tree-branch.model';
import {EntityTreeTabPathStateModel} from '../../entity-element/models/entity-tree-tab-path-state.model';
import {PathStateModel} from '../../entity-element/models/path-state.model';
import {TreeNodeModel} from '../../../../settings/business-settings/models/tree-node-model';
import {BusinessSettingsDisplayDataModel} from '../../../../settings/business-settings/configuration/v1/models/business-settings-display-data.model';
import {EntityTreeBranchTypeEnum} from '../../models/entity-tree-branch-type.enum';
import {EntityTypeEnum} from '../../../../settings/business-settings/configuration/v1/entity-type.enum';

@Injectable({
    providedIn: 'root'
})
export class EntityTreePathStateIdentificationService {

    private inactiveEntities: string[] = [];

    constructor(private entityUtil: EntityTreeUtilService) {
    }

    /**
     * Marks / updates the unfinished paths in a relation tree
     * @param rootTreeNode the {@link TreeNodeModel} of the relation
     * @param entityTree the corresponding entity tree configuration
     * @param entityTreeData the corresponding entity tree data
     */
    identifyUnfinishedPaths(rootTreeNode: TreeNodeModel,
                            entityTree: EntityTreeModel,
                            entityTreeData: BusinessSettingsDisplayDataModel): void {
        this.setUnfinishedPathState(rootTreeNode, entityTree, entityTreeData);
    }

    /**
     * Marks / updates the inactive paths in a relation tree
     * @param rootTreeNode the {@link TreeNodeModel} of the relation
     * @param entityTree the corresponding entity tree configuration
     */
    identifyInactivePaths(rootTreeNode: TreeNodeModel,
                          entityTree: EntityTreeModel): void {

        this.updateInactiveEntities(rootTreeNode, entityTree);
        this.setInactivePathState(rootTreeNode, this.inactiveEntities);

    }

    /**
     * Sets the initial specific branches tabs state for every path in an relation tree
     * @param rootTreeNode the {@link TreeNodeModel} of the relation
     * @param entityTree the corresponding entity tree configuration
     * @param entityTreeData the corresponding complete entity tree display data
     */
    setTreeInitialTabsState(entityTree: EntityTreeModel,
                            rootTreeNode: TreeNodeModel,
                            entityTreeData: BusinessSettingsDisplayDataModel): void {
        if (this.entityUtil.entityTreeHasSpecificBranches(entityTree) && rootTreeNode != null) {
            const lastCommonBranchEntity = this.entityUtil.lastValueInBranch(entityTree.commonBranch,
                    entityTreeData.entitiesOrder.commonBranch);
            rootTreeNode.relationTree.forEach((root) => {
                this.setTreeNodeInitialTabsStateRecursively(root, entityTree, lastCommonBranchEntity.name, entityTreeData);
            });
        }
    }

    /**
     * Toggles a specific branch tab state to active or inactive
     * @param branch the specific branch tab to toggle
     * @param tabParentEntity the parent entity i.e. the last entity from the common branch
     * @param currentTreeNode the tree node of the relation
     * @param entityTree the corresponding entity tree configuration
     * @param active the state to which to toggle the tab
     */
    toggleSpecificBranchTabState(branch: EntityTreeBranchTypeEnum,
                                 tabParentEntity: EntityTreeBranchModel,
                                 currentTreeNode: TreeNodeModel,
                                 entityTree: EntityTreeModel,
                                 active: boolean): void {
        const pathSate = currentTreeNode.relationTree.find((item) => item.code === tabParentEntity.path.code);

        if (pathSate.pathState.tabsState == null && active) {
            pathSate.pathState.tabsState = new Map<string, EntityTreeTabPathStateModel>();

            entityTree.specificBranchOptions.forEach((option) => {

                const optionIsBranch = option.value === branch;

                if (optionIsBranch) {
                    pathSate.pathState.tabsState.set(option.value, {
                        isActive: active,
                        unfinishedPath: {isOrigin: active, isInPath: active}
                    });
                } else {
                    pathSate.pathState.tabsState.set(option.value, {
                        isActive: false,
                        unfinishedPath: {isOrigin: false, isInPath: false}
                    });
                }
            });
        }

        let unfinishedPathState: PathStateModel;

        if (active) {
            unfinishedPathState = {isOrigin: true, isInPath: true};
        } else {
            unfinishedPathState = {isOrigin: false, isInPath: false};
        }

        pathSate.pathState.tabsState.set(branch, {unfinishedPath: unfinishedPathState, isActive: active});
    }

    /**
     * For a given tree node sets its specific branch tabs state
     */
    setTreeNodeInitialTabsStateRecursively(treeNode: TreeNodeModel,
                                           entityTree: EntityTreeModel,
                                           lastCommonBranchEntity: EntityTypeEnum,
                                           entityTreeData: BusinessSettingsDisplayDataModel): void {
        if (treeNode != null && treeNode.relationTree != null) {
            treeNode.relationTree.forEach((entry) => {
                if (entityTree.commonBranch.get(entry.type)?.isBranchFinal) {
                    entry.pathState = {
                        ...entry.pathState,
                        tabsState: this.getInitialTabsState(entry, entityTree, entityTreeData)
                    };
                } else {
                    this.setTreeNodeInitialTabsStateRecursively(entry, entityTree, lastCommonBranchEntity, entityTreeData);
                }
            });
        }
    }

    /**
     * Sets the tabs path state for a given tree node.
     */
    setTreeNodeTabsState(treeNode: TreeNodeModel,
                         entity: EntityTreeBranchModel,
                         entityTree: EntityTreeModel,
                         entityTreeData: BusinessSettingsDisplayDataModel): void {
        if (entity != null) {
            if (entity.isSpecificBranchRoot) {

                if (treeNode != null) {
                    let unfinishedPathState: PathStateModel;

                    if (treeNode.pathState.tabsState == null) {
                        treeNode.pathState.tabsState = new Map<string, EntityTreeTabPathStateModel>();
                    }

                    if (treeNode.relationTree != null && treeNode.relationTree.length > 0) {

                        entityTree.specificBranchOptions.forEach((branch) => {

                            const specificBranchRootEntityType = this.entityUtil.firstValueInBranch(
                                    entityTree.specificBranches.get(branch.value),
                                    entityTreeData.entitiesOrder.specificBranches.get(branch.value)).name;

                            if (treeNode.relationTree.find((item) => item.type === specificBranchRootEntityType)) {
                                unfinishedPathState = {isOrigin: false, isInPath: false};
                            } else {
                                unfinishedPathState = {isOrigin: true, isInPath: true};
                            }

                            treeNode.pathState.tabsState.set(branch.value, {
                                ...treeNode.pathState.tabsState.get(branch.value),
                                unfinishedPath: unfinishedPathState
                            });

                        });
                    } else {
                        entityTree.specificBranchOptions.forEach((branch) => {

                            unfinishedPathState = {isOrigin: true, isInPath: true};

                            treeNode.pathState.tabsState.set(branch.value, {
                                ...treeNode.pathState.tabsState.get(branch.value),
                                unfinishedPath: unfinishedPathState
                            });

                        });
                    }
                }
            }
        }

    }

    /**
     * Returns the initial tabs path state for a given tree node.
     */
    private getInitialTabsState(treeNode: TreeNodeModel,
                                entityTree: EntityTreeModel,
                                entityTreeData: BusinessSettingsDisplayDataModel):
            Map<string, EntityTreeTabPathStateModel> {
        let pathTabsState: Map<string, EntityTreeTabPathStateModel>;

        if (treeNode.pathState != null && treeNode.pathState.tabsState != null) {
            pathTabsState = treeNode.pathState.tabsState;
        } else {
            pathTabsState = new Map<string, EntityTreeTabPathStateModel>();
        }

        const tabsTypes = entityTree.specificBranchOptions.map((option) => option.value);

        tabsTypes.forEach((tab) => {
            pathTabsState.set(tab, {
                isActive: treeNode.activeChildren != null
                        ? treeNode.activeChildren.includes(this.entityUtil.firstValueInBranch(
                                entityTree.specificBranches.get(tab),
                                entityTreeData.entitiesOrder.specificBranches.get(tab))?.name)
                        : false,
                unfinishedPath: {
                    isOrigin: !this.entityHasAllChildren(treeNode),
                    isInPath: !this.entityHasAllChildren(treeNode)
                }
            });
        });

        return pathTabsState;
    }

    /**
     * For a given tree node sets its unfinished path value and recursively sets its descendents unfinished path state value.
     */
    private setTreeNodeUnfinishedPathState(treeNode: TreeNodeModel,
                                           entityTree: EntityTreeModel,
                                           entityTreeData: BusinessSettingsDisplayDataModel): void {
        if (treeNode != null) {
            const hasAllChildren = this.entityHasAllChildren(treeNode);

            if (hasAllChildren) {
                treeNode.pathState = {...treeNode.pathState, unfinishedPath: {isOrigin: false, isInPath: false}};
            } else {
                treeNode.pathState = {...treeNode.pathState, unfinishedPath: {isOrigin: true, isInPath: true}};
            }

            if (treeNode.relationTree != null && treeNode.relationTree.length > 0) {
                treeNode.relationTree.forEach((entry) => {
                    this.setTreeNodeUnfinishedPathState(entry, entityTree, entityTreeData);
                });
            }
        }
    }

    /**
     * Given a root tree node, calls {@link setTreeNodeUnfinishedPathState} for each entry in the relation tree
     */
    private setUnfinishedPathState(rootTreeNode: TreeNodeModel,
                                   entityTree: EntityTreeModel,
                                   entityTreeData: BusinessSettingsDisplayDataModel): void {
        if (rootTreeNode != null && rootTreeNode.relationTree != null) {
            rootTreeNode.relationTree.forEach((entry) => {
                this.setTreeNodeUnfinishedPathState(entry, entityTree, entityTreeData);
            });
        }
    }

    /**
     * Given a root tree node, set the inactive path state for which entry and recursively sets the inactive path state for all descendents
     */
    private setInactivePathState(rootTreeNode: TreeNodeModel, invalidEntities: string[]): void {
        if (rootTreeNode != null && rootTreeNode.relationTree != null) {

            rootTreeNode.relationTree.forEach((entry) => {

                if (invalidEntities.includes(entry.code)) {
                    entry.pathState = {...entry.pathState, inactivePath: {isOrigin: true, isInPath: false}};
                    this.setChildrenInInactivePath(entry, invalidEntities);

                } else {
                    entry.pathState = {...entry.pathState, inactivePath: {isOrigin: false, isInPath: false}};
                    this.setInactivePathState(entry, invalidEntities);
                }

            });
        }
    }

    /**
     * Sets the inactive path state of an entity that has as inactive parent
     */
    private setChildrenInInactivePath(treeNode: TreeNodeModel, invalidEntities: string[]): void {
        if (treeNode != null && treeNode.relationTree != null) {

            treeNode.relationTree.forEach((entry) => {
                const isOrigin = invalidEntities.includes(entry.code);

                entry.pathState = {...entry.pathState, inactivePath: {isOrigin: isOrigin, isInPath: true}};

                this.setChildrenInInactivePath(entry, invalidEntities);
            });
        }
    }

    /**
     * Updates the {@link inactiveEntities} value for the root tree node
     */
    private updateInactiveEntities(rootTreeNode: TreeNodeModel,
                                   entityTree: EntityTreeModel): void {
        this.inactiveEntities = [];
        this.getInactiveEntityElements(rootTreeNode, entityTree);
    }

    /**
     * Given a tree node, sets the {@link inactiveEntities} to an array of all the inactive entities codes that exist in said tree node
     */
    private getInactiveEntityElements(treeNode: TreeNodeModel,
                                      entityTree: EntityTreeModel): void {

        if (treeNode != null && treeNode.relationTree != null) {

            treeNode.relationTree.forEach((entry) => {

                const entryConfiguration = this.entityUtil.getEntityConfiguration(entry.type, entityTree);

                const entity = entryConfiguration.pool.find((item) => item.code === entry.code);

                if (!entity?.active) {
                    if (!this.inactiveEntities.includes(entry.code)) {
                        this.inactiveEntities.push(entry.code);
                    }
                }

                this.getInactiveEntityElements(entry, entityTree);
            });
        }
    }

    /**
     * Returns if an entity has all the configuration children on its treeNode.relationTree
     */
    private entityHasAllChildren(treeNode: TreeNodeModel): boolean {
        let hasAllChildren = true;

        if (treeNode.activeChildren != null) {

            hasAllChildren = false;

            if (treeNode.relationTree != null) {
                hasAllChildren = treeNode.activeChildren
                        .every((activeChild) => treeNode.relationTree.some((child) => child.type === activeChild));
            }
        }
        return hasAllChildren;
    }
}
